<script setup lang="ts">
import { breakpointsTailwind } from '@vueuse/core'
import type { OAuthCredential } from 'firebase/auth'

import type { IOptimizeImageOptions } from '@/composables/elements.client'
import { getHostname } from '~~/src/api'
import { getBreadcrumbsJsonLd } from '~~/src/helpers/serp'

const { colors } = useTheme()

const breakpoints = useBreakpoints(breakpointsTailwind)
const isMobile = breakpoints.smaller('md')

const host = getHostname()
const canonical = `${host}/login`
const title = 'Login to Snack Prompt | Discover The Best ChatGPT Prompts'
const description = 'Login to your account on Snack Prompt. Explore a community-driven platform to discover, upvote, and share the best AI prompts for ChatGPT & Gemini.'.substring(0, 170)

const jsonldBreadcrumbs = getBreadcrumbsJsonLd([
  { name: 'Login to Snack Prompt', url: '/login' },
])

useHead({
  link: [
    { rel: 'canonical', href: canonical },
  ],
  meta: [
    { name: 'og:title', content: title },
    { name: 'og:description', content: description },
    { name: 'og:url', content: canonical },
    { name: 'twitter:title', content: title },
    { name: 'twitter:description', content: description },
    { name: 'twitter:site', content: canonical },
  ],
  script: [
    {
      hid: 'page-json-ld',
      type: 'application/ld+json',
      innerHTML: JSON.stringify([jsonldBreadcrumbs]),
    },
  ],
})

useSeoMeta({
  title,
  description,
  ogTitle: title,
  ogDescription: description,
  ogUrl: canonical,
  twitterSite: canonical,
  twitterTitle: title,
  twitterDescription: description,
})

const router = useRouter()
const route = useRoute()
const showExtensionBanner = useShowExtensionBanner()
const { firebaseUser } = useFirebaseAuth()

const containerRef = ref<HTMLElement>()
const { width } = useElementBounding(containerRef)

const imageOptimizationOptions = computed<IOptimizeImageOptions>(() => {
  return {
    width: isMobile.value
      ? width.value * 1.4
      : width.value,
  }
})

const loginOnDesktopApp = async (url: string, credentials: OAuthCredential) => {
  try {
    if (firebaseUser.value === null)
      throw new Error('User is not logged in')
    if (!credentials.idToken || !credentials.accessToken)
      throw new Error('Credentials are not valid')
    const redirectUrl = new URL(url)
    redirectUrl.searchParams.set('uid', firebaseUser.value.uid)
    redirectUrl.searchParams.set('idToken', credentials.idToken)
    redirectUrl.searchParams.set('accessToken', credentials.accessToken)

    window.open(redirectUrl, '_blank')
  }
  catch (err) {
    console.error(err)
  }
}

async function doRedirect() {
  const platformRedirect = route.query.platform_redirect
  await nextTick()
  return router.push(platformRedirect?.toString() || '/')
}

async function handleGoogleSignIn(credentials: OAuthCredential | false) {
  const redirect = route.query.redirect
  if (redirect && credentials) {
    if (redirect.includes('snackprompt') && typeof redirect === 'string') {
      await loginOnDesktopApp(redirect, credentials)
      return
    }
  }

  doRedirect()
}

onMounted(() => {
  if (firebaseUser.value !== null)
    doRedirect()
})
</script>

<template>
  <NuxtLayout>
    <template #outer-container>
      <main
        ref="containerRef" w-screen grid="~ <md:rows-2 md:cols-2"
        :style="{
          height: `calc(100vh - ${HEADER_HEIGHT}px - ${isMobile ? '1' : (showExtensionBanner ? '51' : '1')}px)`,
        }"
      >
        <div h-full w-full bg-white col center gap-6 p-5>
          <Heading level="2" max-w="280px" text-center>
            Sign in to your Google account to access Snack Prompt
          </Heading>

          <Text :color="colors.bluishDarker">
            Join us as we unlock the full potential of AI.
          </Text>

          <AppButtonGoogle @sign-in="handleGoogleSignIn($event)" />
        </div>

        <img
          :src="optimizeImage('https://snackprompt.com/floating_icons.png', imageOptimizationOptions)"
          class="h-full w-full object-center object-cover bg-light-bluish"
        >
      </main>
    </template>
  </NuxtLayout>
</template>
